import { SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  DEFAULT_ADVANCED_FILTERS,
  countAdvancedFilters,
  type QuoteAdvancedFilters,
} from './quoteFilters';

const SECTION_LABEL = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground';

const EXPIRY_OPTIONS: { value: QuoteAdvancedFilters['expiry']; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: 'soon', label: 'Binnen 7 dagen' },
  { value: 'expired', label: 'Verlopen' },
];

export function QuoteMoreFilters({
  filters,
  onChange,
  resultCount,
  ghostCount,
}: {
  filters: QuoteAdvancedFilters;
  onChange: (filters: QuoteAdvancedFilters) => void;
  resultCount: number;
  ghostCount: number;
}) {
  const activeCount = countAdvancedFilters(filters);
  const patch = (next: Partial<QuoteAdvancedFilters>) => onChange({ ...filters, ...next });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant={activeCount > 0 ? 'secondary' : 'outline'} size="sm" className="h-9 gap-2">
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Meer filters
          {activeCount > 0 && (
            <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[11px] font-bold text-primary-foreground">
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <span className="text-sm font-semibold">Meer filters</span>
          <span className="text-xs text-muted-foreground">
            {activeCount > 0 ? `${activeCount} actief` : 'geen actief'}
          </span>
        </div>

        <div className="flex flex-col gap-3 border-b px-4 py-3">
          <span className={SECTION_LABEL}>Weergave</span>
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="hide-ghosts" className="cursor-pointer text-sm font-normal">
              Spookoffertes verbergen
              {ghostCount > 0 && <span className="ml-1.5 text-xs text-muted-foreground">({ghostCount})</span>}
            </Label>
            <Switch
              id="hide-ghosts"
              checked={filters.hideGhosts}
              onCheckedChange={(checked) => patch({ hideGhosts: checked })}
            />
          </div>
          <p className="text-xs leading-snug text-muted-foreground">
            Spookoffertes zijn concepten die 24 uur na aanmaken nooit zijn geopend of bewerkt. Ze blijven zichtbaar
            in de tab Spookoffertes.
          </p>
        </div>

        <div className="flex flex-col gap-2.5 border-b px-4 py-3">
          <span className={SECTION_LABEL}>Ondertekening</span>
          <label className="flex cursor-pointer items-center gap-2.5 text-sm">
            <Checkbox
              checked={filters.awaitingUbo}
              onCheckedChange={(checked) => patch({ awaitingUbo: checked === true })}
            />
            Wacht op medeondertekening UBO
          </label>
          <label className="flex cursor-pointer items-center gap-2.5 text-sm">
            <Checkbox
              checked={filters.fullySigned}
              onCheckedChange={(checked) => patch({ fullySigned: checked === true })}
            />
            Volledig ondertekend
          </label>
        </div>

        <div className="flex flex-col gap-3 border-b px-4 py-3">
          <span className={SECTION_LABEL}>Verloop &amp; waarde</span>
          <div className="flex flex-wrap items-center gap-1.5">
            {EXPIRY_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => patch({ expiry: option.value })}
                className={cn(
                  'h-7 rounded-full border px-3 text-xs font-medium transition-colors',
                  filters.expiry === option.value
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input bg-background hover:bg-accent',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Input
              inputMode="numeric"
              placeholder="€ min"
              value={filters.minValue}
              onChange={(e) => patch({ minValue: e.target.value })}
              className="h-8"
              aria-label="Minimale waarde in euro"
            />
            <span className="text-xs text-muted-foreground">tot</span>
            <Input
              inputMode="numeric"
              placeholder="Geen max"
              value={filters.maxValue}
              onChange={(e) => patch({ maxValue: e.target.value })}
              className="h-8"
              aria-label="Maximale waarde in euro"
            />
          </div>
          <label className="flex cursor-pointer items-center gap-2.5 text-sm">
            <Checkbox
              checked={filters.outdatedOnly}
              onCheckedChange={(checked) => patch({ outdatedOnly: checked === true })}
            />
            Bevat verouderde productversie
          </label>
        </div>

        <div className="flex items-center justify-between gap-3 bg-muted/50 px-4 py-3">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={() => onChange(DEFAULT_ADVANCED_FILTERS)}
            disabled={activeCount === 0}
          >
            Wis alles
          </Button>
          <span className="text-xs text-muted-foreground">
            {resultCount} {resultCount === 1 ? 'offerte' : 'offertes'}
          </span>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default QuoteMoreFilters;
